import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import type { Reservation, ReservationItem, ReservedQuantityMap } from '../types';
import type { B2BCartItem } from '../types/b2b';

interface CreateB2BReservationData {
  pickup_scheduled_date: string;
  notes?: string;
  items: Array<{
    product_id: string;
    quantity: number;
    unit_price: number;
    total_price: number;
    source_location_id?: string;
  }>;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
}

export const useB2BReservations = () => {
  const { buyer } = useAuth();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [reservedQuantities, setReservedQuantities] = useState<ReservedQuantityMap>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReservations = useCallback(async () => {
    if (!buyer) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('reservations')
        .select(`
          *,
          reservation_items (
            id, reservation_id, product_id, quantity,
            unit_price, total_price, source_location_id, created_at
          )
        `)
        .eq('buyer_id', buyer.id)
        .eq('reservation_source', 'b2b')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setReservations(data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load reservations');
    } finally {
      setLoading(false);
    }
  }, [buyer]);

  const loadReservedQuantities = useCallback(async () => {
    if (!buyer) return;
    try {
      const { data } = await supabase
        .from('reservations')
        .select(`
          status,
          reservation_items ( product_id, quantity )
        `)
        .eq('admin_id', buyer.admin_id)
        .in('status', ['reserved', 'ready']);

      const qtyMap: ReservedQuantityMap = {};
      if (data) {
        data.forEach((r: any) => {
          r.reservation_items?.forEach((item: any) => {
            if (item.product_id) {
              qtyMap[item.product_id] = (qtyMap[item.product_id] || 0) + item.quantity;
            }
          });
        });
      }
      setReservedQuantities(qtyMap);
    } catch (err: any) {
      console.error('Failed to load reserved quantities:', err);
    }
  }, [buyer]);

  useEffect(() => {
    loadReservations();
    loadReservedQuantities();
  }, [loadReservations, loadReservedQuantities]);

  const generateReservationNumber = useCallback(async (): Promise<string> => {
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `B2B-${timestamp}-${random}`;
  }, []);

  const createReservation = useCallback(async (data: CreateB2BReservationData): Promise<{ success: boolean; reservation?: Reservation; error?: string }> => {
    if (!buyer) return { success: false, error: 'Not authenticated as buyer' };

    try {
      const reservationNumber = await generateReservationNumber();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const { data: reservation, error: insertError } = await supabase
        .from('reservations')
        .insert({
          admin_id: buyer.admin_id,
          reservation_number: reservationNumber,
          customer_name: buyer.trade_name,
          customer_email: buyer.email,
          customer_phone: buyer.phone || null,
          customer_address: buyer.shipping_address || null,
          status: 'reserved',
          subtotal: data.subtotal,
          tax_amount: data.tax_amount,
          total_amount: data.total_amount,
          notes: data.notes || null,
          expires_at: expiresAt.toISOString(),
          // `reserved_by` references users(id); B2B buyer ids come from b2b_buyers(id).
          reserved_by: buyer.admin_id,
          reserved_at: new Date().toISOString(),
          buyer_id: buyer.id,
          reservation_source: 'b2b',
          pickup_scheduled_date: data.pickup_scheduled_date,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      const reservationItems = data.items.map(item => ({
        reservation_id: reservation.id,
        product_id: item.product_id,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price,
        source_location_id: item.source_location_id || null,
      }));

      const { error: itemsError } = await supabase
        .from('reservation_items')
        .insert(reservationItems);

      if (itemsError) throw itemsError;

      await supabase.from('activity_logs').insert({
        admin_id: buyer.admin_id,
        user_id: buyer.admin_id,
        action_type: 'reservation_created',
        entity_type: 'reservation',
        entity_id: reservation.id,
        description: `B2B reservation ${reservationNumber} created by ${buyer.trade_name}`,
        metadata: {
          reservation_number: reservationNumber,
          buyer_id: buyer.id,
          buyer_name: buyer.trade_name,
          total_amount: data.total_amount,
          items_count: data.items.length,
          pickup_date: data.pickup_scheduled_date,
        },
        created_at: new Date().toISOString()
      });

      await loadReservations();
      await loadReservedQuantities();

      return { success: true, reservation };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to create reservation' };
    }
  }, [buyer, generateReservationNumber, loadReservations, loadReservedQuantities]);

  const getReservedQuantityForProduct = useCallback((productId: string): number => {
    return reservedQuantities[productId] || 0;
  }, [reservedQuantities]);

  return {
    reservations,
    reservedQuantities,
    loading,
    error,
    loadReservations,
    loadReservedQuantities,
    createReservation,
    getReservedQuantityForProduct,
    refreshReservations: loadReservations,
  };
};
